/**
 * Splits a CAIP-10 account into its CAIP-2 chain ID and address.
 *
 * @param account Account in `namespace:reference:address` form.
 * @returns The parsed account, or `undefined` when any required part is missing.
 *
 * @example
 * `parseCaipAccount("eip155:1:0xabc")` returns
 * `{ chainId: "eip155:1", address: "0xabc" }`.
 */
export function parseCaipAccount(account) {
    const [namespace, reference, address] = account.split(":");
    if (!namespace || !reference || !address)
        return;
    return { chainId: `${namespace}:${reference}`, address };
}
/**
 * Groups approved session addresses by CAIP-2 chain ID.
 *
 * Addresses stay separated by chain because some namespaces, including Cosmos, use a different
 * address for each chain.
 *
 * @returns A new record. Missing namespaces produce an empty object.
 */
export function accountsByChain(namespaces) {
    const out = {};
    for (const namespace of Object.values(namespaces ?? {})) {
        for (const account of namespace.accounts ?? []) {
            const parsed = parseCaipAccount(account);
            if (!parsed)
                continue;
            const addresses = out[parsed.chainId] ?? [];
            if (!addresses.includes(parsed.address))
                addresses.push(parsed.address);
            out[parsed.chainId] = addresses;
        }
    }
    return out;
}
/** JSON-RPC and EIP-1193 error codes thrown directly by Konekt. */
export const RpcErrorCode = {
    /** JSON-RPC invalid parameters. */
    invalidParams: -32602,
    /** EIP-1193 unauthorized: a wallet method was requested without a session. */
    unauthorized: 4100,
    /** EIP-1193 unsupported method or missing EVM read transport. */
    unsupportedMethod: 4200,
};
/** Error with a JSON-RPC or EIP-1193 numeric `code`. */
export class ProviderRpcError extends Error {
    /** JSON-RPC or EIP-1193 error code. */
    code;
    /** Creates a provider error with a machine-readable code and human-readable message. */
    constructor(code, message) {
        super(message);
        this.name = "ProviderRpcError";
        this.code = code;
    }
}
export const TAG = {
    sessionPropose: 1100,
    sessionProposeRes: 1101,
    sessionSettle: 1102,
    sessionSettleRes: 1103,
    sessionRequest: 1108,
    sessionRequestRes: 1109,
    sessionUpdate: 1104,
    sessionUpdateRes: 1105,
    sessionExtend: 1106,
    sessionExtendRes: 1107,
    sessionDelete: 1112,
    sessionPing: 1114,
    sessionPingRes: 1115,
};
/** Default WalletConnect protocol lifetimes, in seconds. */
export const TTL = {
    propose: 300,
    request: 900,
    session: 86400,
    minPublish: 300,
};
//# sourceMappingURL=types.js.map